import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { authHeader, isUnauthorized } from "../utils/staffAuth";

const pathURL = process.env.REACT_APP_API_URL;

/**
 * The thread color a chainstitch customer chose, picked off the studio wall.
 *
 * Calley, 2026-10-31: "can you add that to [the] chainstitch part so we can
 * note what color they chose? please mark which are cotton and which are
 * wool." The box here used to take a swatch number 1 to 6 off Natalie's
 * printed sheet, which told nobody which cone that was: the numbers were
 * never named, so an order said "Thread 3" and the colour had to be guessed
 * from memory weeks later.
 *
 * So this is the cones themselves, the same rows as the Thread tab on
 * Manage -> Second Mark, cut down to what is in the studio
 * (fxrybackend/helper/secondMarkThread.js, forPicking). Picking one records
 * its item number, its colour name and its fiber.
 *
 * Cotton or wool is marked on every swatch because it decides the work, not
 * the look: wool thread goes into a wool sweater, cotton into denim and
 * shirting, and it is the one thing about a cone a photo cannot show.
 *
 * The typed box underneath stays, and stays the only answer when the wall
 * does not have what they want. Which also means that how often it gets used
 * is the answer to what we should be reordering.
 */

const FIBERS = ["Cotton", "Wool"];

/** Only the cones whose fiber or name matches what is typed. */
const matching = (colors, fiber, query) => {
  const text = query.trim().toLowerCase();
  return colors.filter((color) => {
    if (fiber && color.fiber !== fiber) return false;
    if (!text) return true;
    return `${color.name} ${color.code}`.toLowerCase().includes(text);
  });
};

function ThreadPicker({ picked, onPick, typed, onTyped }) {
  const [colors, setColors] = useState(null);
  const [failed, setFailed] = useState(false);
  const [fiber, setFiber] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    let gone = false;
    axios
      .get(`${pathURL}second-mark/thread/colors`, { headers: authHeader() })
      .then((response) => {
        if (!gone) setColors(response.data?.colors || []);
      })
      .catch((error) => {
        // Nothing here blocks the order: the typed box can always carry the
        // answer, and a counter with a customer at it is the wrong place to
        // be stuck. A signed-out device lands here too.
        if (!gone) {
          setFailed(true);
          setColors([]);
          console.error(
            "[THREAD PICKER] Couldn't load the thread colors:",
            isUnauthorized(error) ? "signed out" : error?.message,
          );
        }
      });
    return () => {
      gone = true;
    };
  }, []);

  const shown = useMemo(
    () => matching(colors || [], fiber, query),
    [colors, fiber, query],
  );

  const choose = (color) => {
    // One answer, never two. Tapping a cone drops anything typed, the same
    // way typing drops the cone (utils/chainstitch.js keeps them separate
    // fields so each one stays countable).
    if (picked?.key === color.key) {
      onPick(null);
      return;
    }
    onPick(color);
    if (typed) onTyped("");
  };

  const loading = colors === null;
  const empty = !loading && (colors || []).length === 0;

  return (
    <div className="item-describe-box">
      <h5 className="describe-heading">Thread color</h5>
      <div className="thread-picker">
        {loading && <p className="thread-note">Loading the thread wall...</p>}
        {empty && (
          <p className="thread-note">
            {failed
              ? "Couldn't load the thread wall. Write the color they picked."
              : "No cones are counted into the studio yet. Write the color they picked."}
          </p>
        )}
        {!loading && !empty && (
          <>
            <div className="thread-filters">
              {["", ...FIBERS].map((option) => (
                <button
                  key={option || "all"}
                  type="button"
                  className={`thread-chip${fiber === option ? " chosen" : ""}`}
                  onClick={() => setFiber(option)}
                >
                  {option || "All"}
                </button>
              ))}
              <input
                type="text"
                className="thread-search"
                placeholder="Find a color"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
            <div className="thread-grid">
              {shown.map((color) => (
                <button
                  key={color.key}
                  type="button"
                  className={`thread-cone${
                    picked?.key === color.key ? " chosen" : ""
                  }`}
                  onClick={() => choose(color)}
                  title={`${color.supplier}, ${color.line}`}
                >
                  <span className="thread-swatch">
                    <img src={color.swatch} alt="" loading="lazy" />
                  </span>
                  <span className="thread-name">
                    {color.name || color.code}
                  </span>
                  {/* The item number, because the names repeat and do not
                      identify a cone: there are four wool cones called
                      Melange on the wall, and "Brown" is three more (Calley,
                      31 Oct 2026). The number is what reorders it, what the
                      order page prints and what finds it on the shelf. */}
                  <span className="thread-code">{color.code}</span>
                  <span className="thread-meta">
                    {color.fiber || "fiber unknown"}
                  </span>
                </button>
              ))}
            </div>
            {shown.length === 0 && (
              <p className="thread-note">
                Nothing by that name on the wall. Write it in the box below.
              </p>
            )}
            {picked && (
              <p className="thread-chosen">
                {picked.name} {picked.code}
                {picked.fiber ? `, ${picked.fiber.toLowerCase()}` : ""}
              </p>
            )}
          </>
        )}
        <input
          type="text"
          className="know-more-description thread-other"
          placeholder="Not on the wall? Write the color"
          maxLength={60}
          value={typed}
          onChange={(event) => {
            onTyped(event.target.value);
            if (event.target.value && picked) onPick(null);
          }}
        />
      </div>
    </div>
  );
}

export default ThreadPicker;
